<fb-page-header
    [title]="isNew() ? ('INVITE_USER' | translate) : fullName() || email()"
    [subtitle]="isNew() ? ('INVITE_USER_SUBTITLE' | translate) : email()"
    [backLink]="['/organizations', orgId(), 'admin', 'users']"
    [backLabel]="'USERS' | translate">
    @if (isNew()) {
        <div actions class="flex items-center gap-2">
            <button pButton type="button" severity="secondary" [text]="true" (click)="cancel()">{{ 'CANCEL' | translate }}</button>
            <button pButton type="button" [loading]="saving()" (click)="save()">
                <i class="ph ph-paper-plane-tilt"></i>
                <span>{{ 'SEND_INVITE' | translate }}</span>
            </button>
        </div>
    } @else if (!loading() && !loadError()) {
        <div actions class="flex items-center gap-2">
            <button pButton type="button" severity="secondary" [outlined]="true" (click)="sendPasswordReset()">
                <i class="ph ph-key"></i>
                <span>{{ 'SEND_PASSWORD_RESET' | translate }}</span>
            </button>
            @if (canManage() && !isSelf()) {
                <button pButton type="button" severity="danger" [text]="true" (click)="confirmRemove()">
                    <i class="ph ph-user-minus"></i>
                    <span>{{ 'REMOVE_FROM_ORGANISATION' | translate }}</span>
                </button>
            }
        </div>
    }
</fb-page-header>

@if (loading()) {
    <div class="flex flex-col gap-4 max-w-4xl">
        <p-skeleton height="12rem" borderRadius="12px" />
        <p-skeleton height="18rem" borderRadius="12px" />
    </div>
} @else if (loadError()) {
    <div class="flex flex-col items-center gap-2 py-24 text-text-muted">
        <i class="ph ph-warning-circle text-3xl"></i>
        <p class="text-sm">{{ 'USER_LOAD_FAILED' | translate }}</p>
    </div>
} @else {
    <form class="flex flex-col gap-4 max-w-4xl" (ngSubmit)="save()">
        @if (!canManage()) {
            <div class="flex items-start gap-3 p-4 rounded-md bg-concern-warn border border-concern-warn-border">
                <i class="ph ph-lock text-concern-warn-dot text-lg"></i>
                <p class="text-sm text-concern-warn-title">{{ 'CANNOT_MANAGE_USER_NOTE' | translate }}</p>
            </div>
        }

        <!-- Details -->
        <fb-section-card [title]="'DETAILS' | translate" [description]="'USER_DETAILS_DESC' | translate">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label class="flex flex-col gap-1.5">
                    <span class="text-sm font-medium text-text">{{ 'FIRST_NAME' | translate }}</span>
                    <input
                        pInputText
                        name="firstName"
                        [ngModel]="firstName()"
                        (ngModelChange)="firstName.set($event)"
                        [disabled]="!canManage()"
                        [invalid]="submitted() && errors().firstName"
                        autocomplete="given-name" />
                    @if (submitted() && errors().firstName) {
                        <small class="text-red-500">{{ 'FIELD_REQUIRED' | translate }}</small>
                    }
                </label>
                <label class="flex flex-col gap-1.5">
                    <span class="text-sm font-medium text-text">{{ 'LAST_NAME' | translate }}</span>
                    <input
                        pInputText
                        name="lastName"
                        [ngModel]="lastName()"
                        (ngModelChange)="lastName.set($event)"
                        [disabled]="!canManage()"
                        [invalid]="submitted() && errors().lastName"
                        autocomplete="family-name" />
                    @if (submitted() && errors().lastName) {
                        <small class="text-red-500">{{ 'FIELD_REQUIRED' | translate }}</small>
                    }
                </label>
                <label class="flex flex-col gap-1.5">
                    <span class="text-sm font-medium text-text">{{ 'EMAIL' | translate }}</span>
                    <input
                        pInputText
                        type="email"
                        name="email"
                        [ngModel]="email()"
                        (ngModelChange)="email.set($event)"
                        [disabled]="!isNew()"
                        [invalid]="submitted() && errors().email"
                        autocomplete="email" />
                    @if (submitted() && errors().email) {
                        <small class="text-red-500">{{ 'FIELD_INVALID_EMAIL' | translate }}</small>
                    } @else if (!isNew()) {
                        <small class="text-text-muted">{{ 'EMAIL_IS_USERNAME' | translate }}</small>
                    }
                </label>
                <label class="flex flex-col gap-1.5">
                    <span class="text-sm font-medium text-text">{{ 'PHONE' | translate }}</span>
                    <input
                        pInputText
                        type="tel"
                        name="phone"
                        [ngModel]="phone()"
                        (ngModelChange)="phone.set($event)"
                        [disabled]="!canManage()"
                        autocomplete="tel" />
                </label>
            </div>

            @if (!isNew() && !isSelf() && canManage()) {
                <div class="flex items-center justify-between gap-4 mt-5 pt-5 border-t border-border">
                    <div class="flex flex-col">
                        <span class="text-sm font-medium text-text">{{ 'ACCOUNT_DISABLED' | translate }}</span>
                        <span class="text-xs text-text-muted">{{ 'ACCOUNT_DISABLED_DESC' | translate }}</span>
                    </div>
                    <p-toggleswitch name="disabled" [ngModel]="disabled()" (ngModelChange)="disabled.set($event)" />
                </div>
            }
        </fb-section-card>

        <!-- Role -->
        <fb-section-card [title]="'ROLE' | translate" [description]="'ROLE_DESC' | translate">
            @if (isSelf()) {
                <p class="text-sm text-text-muted mb-4 flex items-center gap-2">
                    <i class="ph ph-info"></i>
                    {{ 'CANNOT_CHANGE_OWN_ROLE' | translate }}
                </p>
            }

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                @for (preset of presets(); track preset.key) {
                    @let selected = activePreset() === preset.key;
                    @let locked = roleLocked() || !preset.grantable;
                    <button
                        type="button"
                        class="text-left flex gap-3 p-4 rounded-md border transition-colors"
                        [class]="
                            selected
                                ? 'border-brand bg-concern-accent'
                                : 'border-border bg-surface hover:border-border-strong'
                        "
                        [class.opacity-50]="locked && !selected"
                        [class.cursor-not-allowed]="locked"
                        [disabled]="locked"
                        (click)="applyPreset(preset.key)">
                        <span
                            class="size-9 shrink-0 rounded-md flex items-center justify-center text-lg"
                            [class]="selected ? 'bg-brand text-brand-ink' : 'bg-surface-2 text-text-2'">
                            <i [class]="'ph ' + preset.icon"></i>
                        </span>
                        <span class="flex flex-col gap-1 min-w-0">
                            <span class="font-semibold text-text flex items-center gap-2">
                                {{ 'ROLE_' + preset.key | translate }}
                                @if (selected) {
                                    <i class="ph-fill ph-check-circle text-brand"></i>
                                }
                            </span>
                            <span class="text-xs text-text-muted">{{ 'ROLE_' + preset.key + '_DESC' | translate }}</span>
                            @if (!preset.grantable) {
                                <span class="text-xs text-concern-warn-title">{{ 'CANNOT_GRANT_ROLE' | translate }}</span>
                            }
                        </span>
                    </button>
                }
            </div>

            @if (!activePreset() && scopes().length > 0) {
                <p class="text-sm text-text-muted mt-3 flex items-center gap-2">
                    <i class="ph ph-sliders-horizontal"></i>
                    {{ 'ROLE_CUSTOM_ACTIVE' | translate }}
                </p>
            }

            <button
                type="button"
                class="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-text-2 hover:text-text"
                (click)="showAdvanced.update((v) => !v)">
                <i [class]="showAdvanced() ? 'ph ph-caret-down' : 'ph ph-caret-right'"></i>
                {{ 'ADVANCED_PERMISSIONS' | translate }}
            </button>

            @if (showAdvanced()) {
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                    @for (option of scopeOptions(); track option.key) {
                        @let locked = roleLocked() || !option.grantable;
                        <label
                            class="flex items-start gap-3 p-3 rounded-md border border-border"
                            [class.opacity-50]="locked"
                            [class.cursor-pointer]="!locked">
                            <p-checkbox
                                [binary]="true"
                                [ngModel]="hasScope(option.key) || hasScope('super')"
                                [ngModelOptions]="{ standalone: true }"
                                [disabled]="locked"
                                (onChange)="toggleScope(option.key)" />
                            <span class="flex flex-col gap-0.5">
                                <span class="text-sm font-medium text-text flex items-center gap-1.5">
                                    <i [class]="'ph ' + option.icon"></i>
                                    {{ 'SCOPE_' + option.key.toUpperCase() | translate }}
                                </span>
                                <span class="text-xs text-text-muted">{{ 'SCOPE_' + option.key.toUpperCase() + '_DESC' | translate }}</span>
                            </span>
                        </label>
                    }
                </div>
            }
        </fb-section-card>

        <!-- Venue access -->
        <fb-section-card [title]="'VENUE_ACCESS' | translate" [description]="'VENUE_ACCESS_DESC' | translate">
            @if (hasScope('super')) {
                <p class="text-sm text-text-muted flex items-center gap-2">
                    <i class="ph ph-buildings"></i>
                    {{ 'OWNER_HAS_ALL_VENUES' | translate }}
                </p>
            } @else {
                <div class="flex items-center justify-between mb-3">
                    <span class="text-sm text-text-muted">{{ venueIds().length }} / {{ venues().length }}</span>
                    <button
                        pButton
                        type="button"
                        size="small"
                        [text]="true"
                        [disabled]="roleLocked()"
                        (click)="toggleAllVenues()">
                        {{ (allVenuesSelected() ? 'DESELECT_ALL' : 'SELECT_ALL') | translate }}
                    </button>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    @for (venue of venues(); track venue._id) {
                        <label
                            class="flex items-center gap-3 p-3 rounded-md border border-border"
                            [class.cursor-pointer]="!roleLocked()"
                            [class.opacity-60]="roleLocked()">
                            <p-checkbox
                                [binary]="true"
                                [ngModel]="venueIds().includes(venue._id)"
                                [ngModelOptions]="{ standalone: true }"
                                [disabled]="roleLocked()"
                                (onChange)="toggleVenue(venue._id)" />
                            <span class="text-sm text-text truncate">{{ venue.name }}</span>
                        </label>
                    } @empty {
                        <p class="text-sm text-text-muted">{{ 'NO_VENUES_IN_ORG' | translate }}</p>
                    }
                </div>
            }
        </fb-section-card>

        <fb-save-bar [visible]="canManage() && dirty()" [saving]="saving()" (save)="save()" (discard)="discard()" />
    </form>
}
